import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, SimpleGrid, Icon, Progress, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, Tooltip,
} from '@chakra-ui/react';
import { FiRefreshCw, FiHardDrive, FiAlertTriangle, FiInfo } from 'react-icons/fi';

const API_BASE = '/api/v1';
const POLL_INTERVAL_MS = 15000;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

const formatBytes = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)} ${units[i]}`;
};

const formatBytesPerSec = (bytesPerSec) => {
  if (bytesPerSec == null) return '—';
  const sign = bytesPerSec < 0 ? '-' : '';
  return `${sign}${formatBytes(Math.abs(bytesPerSec))}/s`;
};

const usageColor = (percent) => {
  if (percent == null) return 'gray';
  if (percent >= 90) return 'red';
  if (percent >= 80) return 'yellow';
  return 'green';
};

const statusColor = (status) => {
  if (status === 'Bound') return 'green';
  if (status === 'Pending') return 'yellow';
  return 'red';
};

function PersistentVolumes() {
  const [volumes, setVolumes] = useState([]);
  const [namespace, setNamespace] = useState('');
  const [growthRates, setGrowthRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastSampleRef = useRef({});

  const fetchVolumes = useCallback(() => {
    fetch(`${API_BASE}/k8s/volumes/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const fetchedVolumes = data.volumes || [];
        setVolumes(fetchedVolumes);
        setNamespace(data.namespace || '');
        setLoading(false);
        setError(null);

        const now = Date.now();
        const rates = {};
        fetchedVolumes.forEach((vol) => {
          const used = vol.usage?.used_bytes;
          const prev = lastSampleRef.current[vol.name];
          if (prev && used != null) {
            const elapsedSeconds = (now - prev.timestamp) / 1000;
            if (elapsedSeconds > 0) {
              rates[vol.name] = (used - prev.used) / elapsedSeconds;
            }
          }
          if (used != null) {
            lastSampleRef.current[vol.name] = { used, timestamp: now };
          }
        });
        setGrowthRates((current) => ({ ...current, ...rates }));
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchVolumes();
    const interval = setInterval(fetchVolumes, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchVolumes]);

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  const withUsage = volumes.filter(v => v.usage != null);
  const over80 = withUsage.filter(v => v.usage.percent_used >= 80 && v.usage.percent_used < 90).length;
  const over90 = withUsage.filter(v => v.usage.percent_used >= 90).length;
  const totalCapacity = withUsage.reduce((sum, v) => sum + (v.usage.capacity_bytes || 0), 0);
  const totalUsed = withUsage.reduce((sum, v) => sum + (v.usage.used_bytes || 0), 0);

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Persistent Volumes</Heading>
          <Text color="gray.500">
            {volumes.length} volume claims in namespace <Badge colorScheme="blue">{namespace}</Badge>
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchVolumes}>Refresh</Button>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Box bg="white" p={4} borderRadius="lg" boxShadow="md">
          <Text fontSize="xs" color="gray.400" mb={1}>Total Capacity</Text>
          <Text fontSize="xl" fontWeight="bold">{formatBytes(totalCapacity)}</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="lg" boxShadow="md">
          <Text fontSize="xs" color="gray.400" mb={1}>Total Used</Text>
          <Text fontSize="xl" fontWeight="bold">{formatBytes(totalUsed)}</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="lg" boxShadow="md">
          <HStack spacing={1} mb={1}>
            <Icon as={FiAlertTriangle} boxSize={3.5} color="yellow.500" />
            <Text fontSize="xs" color="gray.400">&gt;80% Full</Text>
          </HStack>
          <Text fontSize="xl" fontWeight="bold" color={over80 > 0 ? 'yellow.600' : 'inherit'}>{over80}</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="lg" boxShadow="md">
          <HStack spacing={1} mb={1}>
            <Icon as={FiAlertTriangle} boxSize={3.5} color="red.500" />
            <Text fontSize="xs" color="gray.400">&gt;90% Full</Text>
          </HStack>
          <Text fontSize="xl" fontWeight="bold" color={over90 > 0 ? 'red.600' : 'inherit'}>{over90}</Text>
        </Box>
      </SimpleGrid>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>PVC (PV)</Th>
              <Th>Status</Th>
              <Th>Storage Class</Th>
              <Th>Capacity</Th>
              <Th>Used</Th>
              <Th>% Used</Th>
              <Th>
                <HStack spacing={1}>
                  <Text>Growth Rate</Text>
                  <Tooltip label="Change in used bytes since the last poll. Kubelet does not report per-volume I/O throughput or IOPS, so this tracks space growth, not disk I/O.">
                    <span><Icon as={FiInfo} boxSize={3} color="gray.400" /></span>
                  </Tooltip>
                </HStack>
              </Th>
              <Th>Node</Th>
              <Th>Access Modes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {volumes.map((vol) => (
              <Tr key={vol.name} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium" fontSize="sm">{vol.name}</Text>
                    <Text fontSize="xs" color="gray.400" isTruncated maxW="220px">{vol.pv_name || '—'}</Text>
                  </VStack>
                </Td>
                <Td>
                  <Badge colorScheme={statusColor(vol.status)}>
                    <Icon as={FiHardDrive} mr={1} boxSize={3} />
                    {vol.status}
                  </Badge>
                </Td>
                <Td><Text fontSize="sm">{vol.storage_class || '—'}</Text></Td>
                <Td><Text fontSize="sm">{vol.capacity || vol.requested_capacity || '—'}</Text></Td>
                <Td><Text fontSize="sm">{formatBytes(vol.usage?.used_bytes)}</Text></Td>
                <Td minW="140px">
                  {vol.usage?.percent_used != null ? (
                    <VStack align="stretch" spacing={1}>
                      <Progress
                        value={vol.usage.percent_used}
                        size="sm"
                        borderRadius="full"
                        colorScheme={usageColor(vol.usage.percent_used)}
                      />
                      <Text fontSize="xs" color="gray.500">{vol.usage.percent_used.toFixed(1)}%</Text>
                    </VStack>
                  ) : <Text fontSize="sm" color="gray.400">—</Text>}
                </Td>
                <Td>
                  <Text fontSize="sm" color={growthRates[vol.name] > 0 ? 'orange.600' : 'gray.600'}>
                    {formatBytesPerSec(growthRates[vol.name])}
                  </Text>
                </Td>
                <Td><Text fontSize="xs" color="gray.500">{vol.usage?.node_name || '—'}</Text></Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    {(vol.access_modes || []).map((mode) => (
                      <Badge key={mode} colorScheme="purple" fontSize="xs">{mode}</Badge>
                    ))}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default PersistentVolumes;
